import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import AdminNav from "../../components/AdminNav";
import api from "../../utils/api";

export default function ImageManager() {
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);
  const [linkResults, setLinkResults] = useState(null);

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      const formData = new FormData();
      
      // Append files with their relative paths preserved
      files.forEach((file) => {
        // Get the webkitRelativePath which preserves folder structure
        const relativePath = file.webkitRelativePath || file.name;
        formData.append("images", file);
        formData.append("paths", relativePath); // Send paths separately
      });

      const response = await api.post("/upload/images", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const newImages = response.data.urls.map((url, index) => ({
        url,
        name: url.split("/").pop(),
        path: files[index].webkitRelativePath || files[index].name,
        selected: false,
      }));

      setUploadedImages([...newImages, ...uploadedImages]);
      toast.success(`${response.data.uploaded} image(s) uploaded successfully!`);

      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} image(s) failed to upload`);
      }

      // Reset file input
      e.target.value = "";
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  const toggleImageSelection = (url) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(url)) {
      newSelected.delete(url);
    } else {
      newSelected.add(url);
    }
    setSelectedImages(newSelected);
  };

  const selectAll = () => {
    if (selectedImages.size === uploadedImages.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(uploadedImages.map((img) => img.url)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedImages.size === 0) {
      toast.error("No images selected");
      return;
    }

    if (!window.confirm(`Delete ${selectedImages.size} selected image(s)? This cannot be undone.`)) {
      return;
    }

    setDeleting(true);

    try {
      const deletePromises = Array.from(selectedImages).map((url) => api.delete("/upload/image", { data: { url } }));

      await Promise.all(deletePromises);

      setUploadedImages(uploadedImages.filter((img) => !selectedImages.has(img.url)));
      setSelectedImages(new Set());
      toast.success(`${deletePromises.length} image(s) deleted successfully`);
    } catch (error) {
      console.error("Error deleting images:", error);
      toast.error("Failed to delete some images");
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard!");
  };

  const copyAllUrls = () => {
    const urls = uploadedImages.map((img) => img.url).join("\n");
    navigator.clipboard.writeText(urls);
    toast.success(`${uploadedImages.length} URLs copied to clipboard!`);
  };

  const handleAutoLinkImages = async () => {
    if (uploadedImages.length === 0) {
      toast.error("No images to link");
      return;
    }

    if (!window.confirm("Auto-link images to products based on SKU in path? This will analyze paths like 'products/2794/F2794-2/1.jpg' and create product_images entries.")) {
      return;
    }

    setAutoLinking(true);

    try {
      const response = await api.post("/upload/auto-link", {
        images: uploadedImages.map((img) => ({
          url: img.url,
          path: img.path,
        })),
      });

      setLinkResults(response.data);
      toast.success(`Linked ${response.data.linked} images to ${response.data.products_updated} products!`);

      if (response.data.skipped > 0) {
        toast.warning(`${response.data.skipped} images skipped (SKU not found or invalid path)`);
      }
    } catch (error) {
      console.error("Error auto-linking images:", error);
      toast.error("Failed to auto-link images");
    } finally {
      setAutoLinking(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <AdminNav />
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="heading-metallic text-4xl mb-2">Bulk Image Manager</h1>
            <p className="text-gray-400">Upload, manage, and delete product images stored in Cloudflare R2</p>
          </div>

          {/* Upload Section */}
          <div className="card mb-8">
            <h2 className="heading-metallic-soft text-2xl mb-6">Upload Images</h2>

            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                <h3 className="font-bold text-blue-400 mb-2">📁 Folder Structure Guide</h3>
                <p className="text-sm text-gray-300 mb-2">
                  Upload folders with this structure to auto-link images to products:
                </p>
                <code className="block bg-gradient-to-r from-black via-gray-900 to-black text-gold-400 p-3 rounded text-sm font-mono">
                  2794/F2794-2/1.jpg<br />
                  2794/F2794-2/2.jpg<br />
                  2795/F2795-1/1.jpg
                </code>
                <ul className="text-sm text-gray-400 mt-3 space-y-1">
                  <li>• <strong>2794</strong> = Base SKU folder</li>
                  <li>• <strong>F2794-2</strong> = Product SKU (must match database)</li>
                  <li>• <strong>1.jpg, 2.jpg</strong> = Image files (numbered)</li>
                </ul>
              </div>

              {/* File Upload */}
              <div>
                <label className={`btn-primary cursor-pointer inline-block ${uploading ? "opacity-50" : ""}`}>
                  {uploading ? "Uploading..." : "📤 Select Folder to Upload"}
                  <input 
                    type="file" 
                    accept="image/*" 
                    multiple 
                    webkitdirectory="" 
                    directory=""
                    onChange={handleBulkUpload} 
                    disabled={uploading} 
                    className="hidden" 
                  />
                </label>
                <p className="text-sm text-gray-500 mt-2">Select a folder containing product images with SKU structure</p>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="bg-gradient-to-r from-black via-gray-900 to-black border border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gold-500"></div>
                    <span className="text-gray-300">Uploading images to R2...</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Image Gallery */}
          {uploadedImages.length > 0 && (
            <div className="card">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gold-500">
                  Uploaded Images ({uploadedImages.length})
                </h2>
                <div className="flex gap-4">
                  <button onClick={handleAutoLinkImages} disabled={autoLinking} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded">
                    {autoLinking ? "Linking..." : "🔗 Auto-Link to Products"}
                  </button>
                  <button onClick={selectAll} className="btn-secondary">
                    {selectedImages.size === uploadedImages.length ? "Deselect All" : "Select All"}
                  </button>
                  {uploadedImages.length > 0 && (
                    <button onClick={copyAllUrls} className="btn-secondary">
                      📋 Copy All URLs
                    </button>
                  )}
                  {selectedImages.size > 0 && (
                    <button onClick={handleDeleteSelected} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded">
                      {deleting ? "Deleting..." : `🗑️ Delete Selected (${selectedImages.size})`}
                    </button>
                  )}
                </div>
              </div>

              {/* Link Results */}
              {linkResults && (
                <div className="mb-6 bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                  <h3 className="font-bold text-green-400 mb-2">✅ Auto-Link Results</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Images Linked:</span>{" "}
                      <span className="text-white font-bold">{linkResults.linked}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Products Updated:</span>{" "}
                      <span className="text-white font-bold">{linkResults.products_updated}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Skipped:</span>{" "}
                      <span className="text-yellow-400 font-bold">{linkResults.skipped}</span>
                    </div>
                  </div>
                  {linkResults.details && linkResults.details.length > 0 && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-sm text-gray-400 hover:text-white">View Details</summary>
                      <div className="mt-2 max-h-40 overflow-y-auto text-xs font-mono bg-gradient-to-r from-black via-gray-900 to-black p-2 rounded">
                        {linkResults.details.map((detail, i) => (
                          <div key={i} className={detail.success ? "text-green-400" : "text-yellow-400"}>
                            {detail.message}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Image Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {uploadedImages.map((image, index) => (
                  <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.05 }} className={`relative group ${selectedImages.has(image.url) ? "ring-2 ring-gold-500" : ""}`}>
                    {/* Selection Checkbox */}
                    <div className="absolute top-2 left-2 z-10">
                      <input type="checkbox" checked={selectedImages.has(image.url)} onChange={() => toggleImageSelection(image.url)} className="w-5 h-5 cursor-pointer" />
                    </div>

                    {/* Image */}
                    <div className="aspect-square bg-gradient-to-r from-black via-gray-900 to-black rounded-lg overflow-hidden border border-gray-700">
                      <img src={image.url} alt={image.name} className="w-full h-full object-cover" onError={(e) => {
                        e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23374151' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%239CA3AF' dy='.3em'%3EError%3C/text%3E%3C/svg%3E";
                      }} />
                    </div>

                    {/* Image Actions */}
                    <div className="mt-2 space-y-2">
                      <div className="text-xs text-gray-500 truncate" title={image.path || image.name}>
                        {image.path || image.name}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => copyToClipboard(image.url)}
                          className="flex-1 bg-gray-800 hover:bg-gray-700 text-xs py-1 px-2 rounded"
                          title="Copy URL"
                        >
                          📋 Copy URL
                        </button>
                        <a href={image.url} target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-gray-700 text-xs py-1 px-2 rounded" title="Open in new tab">
                          🔗
                        </a>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {uploadedImages.length === 0 && (
            <div className="card text-center py-12">
              <div className="text-6xl mb-4">📸</div>
              <h3 className="text-xl font-bold text-gray-400 mb-2">No Images Yet</h3>
              <p className="text-gray-500">Upload images to get started</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
